import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ImageIcon from "@mui/icons-material/Image";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import api from "../../api/api";
import { clearCache } from "../../utils/cache";
import { useSnackbar } from "../../utils/snackbar";

const PAGE_SIZE = 20;
const MAX_FLASH = 50;
const CACHE_KEY = "admin_flash_deals_v3";
const BRAND_GRAD = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

function flashStatus(sel) {
  if (!sel?.isFlashDeal || !sel?.startAt || !sel?.endAt) return "off";
  const now = Date.now();
  const start = new Date(sel.startAt).getTime();
  const end = new Date(sel.endAt).getTime();
  if (now < start) return "scheduled";
  if (now > end) return "expired";
  return "active";
}

const statusColor = { active: "warning", scheduled: "info", expired: "default", off: "default" };
const statusLabel = { active: "Live", scheduled: "Scheduled", expired: "Expired", off: "Off" };

function ProductThumb({ src, title, size = 48 }) {
  const theme = useTheme();
  if (!src) {
    return (
      <Avatar
        variant="rounded"
        sx={{ width: size, height: size, bgcolor: "action.hover" }}
      >
        <ImageIcon sx={{ color: "text.disabled", fontSize: size * 0.5 }} />
      </Avatar>
    );
  }
  return (
    <Avatar
      variant="rounded"
      src={src}
      alt={title}
      sx={{ width: size, height: size, bgcolor: "action.hover" }}
    />
  );
}

export default function AdminFlashDeals() {
  const theme = useTheme();
  const { showAlert } = useSnackbar();

  const [pageProducts, setPageProducts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // { [productId]: { isFlashDeal, flashDealOrder, discountPercent, startAt, endAt } }
  const [selections, setSelections] = useState({});

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Bulk bar inputs
  const [bulkDiscount, setBulkDiscount] = useState(20);
  const [bulkStart, setBulkStart] = useState(() => new Date().toISOString().slice(0, 16));
  const [bulkEnd, setBulkEnd] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 16));

  // Drag-to-reorder state for active flash list
  const [flashListOverride, setFlashListOverride] = useState(null);
  const [dragOverFlashId, setDragOverFlashId] = useState(null);
  const [flashOrderDirty, setFlashOrderDirty] = useState(false);
  const [savingFlashOrder, setSavingFlashOrder] = useState(false);
  const draggedFlashIdRef = useRef(null); // ref avoids stale closure in onDrop

  // Quick Picks state
  const [allQuickPicks, setAllQuickPicks] = useState([]);
  const [dragOverQPId, setDragOverQPId] = useState(null);
  const [qpListOverride, setQpListOverride] = useState(null);
  const [qpOrderDirty, setQpOrderDirty] = useState(false);
  const [savingQPOrder, setSavingQPOrder] = useState(false);
  const [qpSearch, setQpSearch] = useState("");
  const draggedQPIdRef = useRef(null);

  const loadAllQuickPicks = useCallback(async () => {
    try {
      const res = await api.get("/api/products/all");
      const data = res.data?.data ?? [];
      const picks = data
        .filter((p) => p.isQuickPick)
        .sort((a, b) => (a.quickPickOrder ?? 999) - (b.quickPickOrder ?? 999));
      setAllQuickPicks(picks);
      setQpListOverride(null);
    } catch {}
  }, []);

  useEffect(() => { loadAllQuickPicks(); }, [loadAllQuickPicks]);

  const saveQPOrder = async () => {
    const list = qpListOverride ?? allQuickPicks;
    setSavingQPOrder(true);
    try {
      await api.put("/api/admin/products/quick-picks/reorder", { ids: list.map((p) => p._id) });
      setQpOrderDirty(false);
      loadAllQuickPicks();
      showAlert("Quick Picks order saved!", "success");
    } catch {
      showAlert("Failed to save order.", "error");
    } finally {
      setSavingQPOrder(false);
    }
  };

  const removeFromQuickPicks = async (id) => {
    try {
      await api.put(`/api/admin/products/quick-picks/toggle/${id}`, { enabled: false });
      loadAllQuickPicks();
      showAlert("Removed from Quick Picks", "success");
    } catch {
      showAlert("Failed to remove", "error");
    }
  };

  const addToQuickPicks = async (product) => {
    try {
      await api.put(`/api/admin/products/quick-picks/toggle/${product._id}`, { enabled: true });
      loadAllQuickPicks();
      showAlert(`${product.title} added to Quick Picks`, "success");
    } catch {
      showAlert("Failed to add", "error");
    }
  };

  /* ── Load products ── */
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/api/products", { params: { page, limit: PAGE_SIZE, search } });
        if (!active) return;
        const incoming = res.data.data || [];
        setPageProducts(incoming);
        setMeta(res.data.meta);

        setSelections((prev) => {
          const next = { ...prev };
          incoming.forEach((p) => {
            if (!(p._id in next)) {
              next[p._id] = {
                isFlashDeal: !!p.isFlashDeal,
                flashDealOrder: p.flashDealOrder ?? null,
                discountPercent: p.flashDeal?.discountPercent ?? 20,
                startAt: p.flashDeal?.startAt
                  ? new Date(p.flashDeal.startAt).toISOString().slice(0, 16)
                  : new Date().toISOString().slice(0, 16),
                endAt: p.flashDeal?.endAt
                  ? new Date(p.flashDeal.endAt).toISOString().slice(0, 16)
                  : new Date(Date.now() + 86400000).toISOString().slice(0, 16),
              };
            }
          });
          return next;
        });
      } catch {
        showAlert("Failed to load products", "error");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, search]);

  // Dedicated load of ALL active flash deals for the right panel (not page-filtered)
  const [allFlashDeals, setAllFlashDeals] = useState([]);
  const loadAllFlashDeals = useCallback(async () => {
    try {
      const res = await api.get("/api/products/all");
      const data = res.data?.data ?? [];
      const flash = data
        .filter((p) => p.isFlashDeal)
        .sort((a, b) => (a.flashDealOrder ?? 999) - (b.flashDealOrder ?? 999));
      setAllFlashDeals(flash.map((p) => ({
        id: p._id,
        product: p,
        sel: {
          isFlashDeal: true,
          flashDealOrder: p.flashDealOrder ?? null,
          discountPercent: p.flashDeal?.discountPercent ?? 20,
          startAt: p.flashDeal?.startAt ?? null,
          endAt: p.flashDeal?.endAt ?? null,
        },
      })));
      setFlashListOverride(null); // reset drag state when data refreshes
    } catch {}
  }, []);

  useEffect(() => { loadAllFlashDeals(); }, [loadAllFlashDeals]);

  /* ── Merged view ── */
  const paginated = useMemo(
    () => pageProducts.map((p) => ({ ...p, _sel: selections[p._id] })),
    [pageProducts, selections]
  );

  const flashCount = useMemo(() => allFlashDeals.length, [allFlashDeals]);


  /* ── Bulk selection ── */
  const visibleIds = paginated.map((p) => p._id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selectedIds.includes(id));

  const handleHeaderCheckbox = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => {
        const added = visibleIds.filter((id) => !prev.includes(id));
        return [...prev, ...added];
      });
    }
  };

  const handleRowCheckbox = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ── Bulk actions ── */
  const bulkEnableFlash = () => {
    setSelections((prev) => {
      const usedOrders = new Set(
        Object.entries(prev)
          .filter(([pid, s]) => s?.isFlashDeal && !selectedIds.includes(pid))
          .map(([, s]) => Number(s.flashDealOrder))
          .filter(Boolean)
      );
      let nextOrder = usedOrders.size > 0 ? Math.max(...usedOrders) + 1 : 1;
      const next = { ...prev };
      for (const id of selectedIds) {
        while (usedOrders.has(nextOrder)) nextOrder++;
        usedOrders.add(nextOrder);
        next[id] = { ...(next[id] || {}), isFlashDeal: true, flashDealOrder: nextOrder };
        nextOrder++;
      }
      return next;
    });
    setSelectedIds([]);
  };

  const bulkDisableFlash = () => {
    setSelections((prev) => {
      const next = { ...prev };
      for (const id of selectedIds) {
        next[id] = { ...(next[id] || {}), isFlashDeal: false, flashDealOrder: null };
      }
      return next;
    });
    setSelectedIds([]);
  };

  const bulkApplyFields = () => {
    setSelections((prev) => {
      const next = { ...prev };
      for (const id of selectedIds) {
        if (next[id]?.isFlashDeal) {
          next[id] = { ...next[id], discountPercent: bulkDiscount, startAt: bulkStart, endAt: bulkEnd };
        }
      }
      return next;
    });
    setSelectedIds([]);
  };

  /* ── Toggle / field actions ── */
  const toggleFlash = (id, enabled) => {
    setSelections((prev) => {
      const usedOrders = new Set(
        Object.entries(prev)
          .filter(([pid, s]) => s?.isFlashDeal && pid !== id)
          .map(([, s]) => Number(s.flashDealOrder))
          .filter(Boolean)
      );
      let nextOrder = 1;
      while (usedOrders.has(nextOrder)) nextOrder++;

      if (enabled && nextOrder > MAX_FLASH) {
        showAlert(`Maximum of ${MAX_FLASH} flash deals reached.`, "error");
        return prev;
      }
      return {
        ...prev,
        [id]: { ...(prev[id] || {}), isFlashDeal: enabled, flashDealOrder: enabled ? nextOrder : null },
      };
    });
  };

  const setField = (id, field, value) =>
    setSelections((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const setOrder = (id, raw) => {
    const order = Number(raw);
    if (!Number.isInteger(order) || order < 1 || order > MAX_FLASH) {
      showAlert(`Order must be between 1 and ${MAX_FLASH}.`, "error");
      return;
    }
    const conflict = Object.entries(selections).some(
      ([pid, s]) => pid !== id && s?.isFlashDeal && Number(s.flashDealOrder) === order
    );
    if (conflict) {
      showAlert(`Order ${order} is already taken.`, "error");
      return;
    }
    setField(id, "flashDealOrder", order);
  };

  const removeFromFlash = (id) => toggleFlash(id, false);

  /* ── Save ── */
  const saveChanges = async () => {
    setSaving(true);
    const toSave = pageProducts.filter((p) => {
      const sel = selections[p._id];
      if (!sel) return false;
      const orig = {
        isFlashDeal: !!p.isFlashDeal,
        flashDealOrder: p.flashDealOrder ?? null,
        discountPercent: p.flashDeal?.discountPercent,
        startAt: p.flashDeal?.startAt,
        endAt: p.flashDeal?.endAt,
      };
      return JSON.stringify(sel) !== JSON.stringify(orig);
    });

    try {
      for (const p of toSave) {
        const sel = selections[p._id];
        await api.put(`/api/products/${p._id}/flash-deal`, {
          enabled: sel.isFlashDeal,
          flashDealOrder: sel.flashDealOrder,
          discountPercent: sel.discountPercent,
          startAt: sel.startAt,
          endAt: sel.endAt,
        });
      }
      clearCache(CACHE_KEY);
      loadAllFlashDeals();
      showAlert("Flash deals saved successfully!", "success");
    } catch {
      showAlert("Failed to save flash deals.", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveFlashOrder = async () => {
    const list = flashListOverride ?? allFlashDeals;
    setSavingFlashOrder(true);
    try {
      await api.put("/api/admin/products/flash-deals/reorder", { ids: list.map((x) => x.id) });
      setFlashOrderDirty(false);
      loadAllFlashDeals();
      showAlert("Flash deal order saved!", "success");
    } catch {
      showAlert("Failed to save order.", "error");
    } finally {
      setSavingFlashOrder(false);
    }
  };

  const progress = (flashCount / MAX_FLASH) * 100;

  /* ── Skeleton rows ── */
  const SkeletonRows = () =>
    [...Array(6)].map((_, i) => (
      <TableRow key={i}>
        <TableCell colSpan={8}>
          <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
        </TableCell>
      </TableRow>
    ));

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1500, mx: "auto" }}>

        {/* ── HEADER ── */}
        <Fade in timeout={400}>
          <Card
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              bgcolor: "background.paper",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack spacing={3}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={2}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      background: BRAND_GRAD,
                      borderRadius: 2,
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <LocalFireDepartmentIcon sx={{ color: "white", fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ background: BRAND_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                    >
                      Flash Deals
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      Set discounts, schedules and display order for time-limited deals
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Tooltip title="Flash deals render on the home screen in the order you set here">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={saveChanges}
                    disabled={saving}
                    sx={{
                      background: BRAND_GRAD,
                      boxShadow: "0 4px 14px rgba(102,126,234,0.45)",
                      px: 3,
                      py: 1.25,
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { background: "linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)" },
                    }}
                  >
                    {saving ? "Saving…" : "Save Changes"}
                  </Button>
                </Stack>
              </Stack>

              <Divider />

              {/* Slot counter */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Flash deal slots used
                  </Typography>
                  <Chip
                    label={`${flashCount} / ${MAX_FLASH}`}
                    size="small"
                    sx={{
                      background: progress > 80 ? "linear-gradient(135deg,#f093fb,#f5576c)" : BRAND_GRAD,
                      color: "white",
                      fontWeight: 700,
                    }}
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(progress, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 5,
                    bgcolor: "action.hover",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 5,
                      background: progress > 80 ? "linear-gradient(90deg,#f093fb,#f5576c)" : BRAND_GRAD,
                    },
                  }}
                />
              </Box>
            </Stack>
          </Card>
        </Fade>

        {/* ── TWO-PANEL LAYOUT ── */}
        <Grid container spacing={3}>

          {/* LEFT PANEL — catalog browser */}
          <Grid item xs={12} lg={7}>
            <Stack spacing={2}>

              {/* Search */}
              <Card elevation={0} sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search products by name or category…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Card>

              {/* Bulk action bar */}
              {selectedIds.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: "action.selected",
                    border: "1.5px solid",
                    borderColor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Chip
                    label={`${selectedIds.length} selected`}
                    size="small"
                    sx={{ background: BRAND_GRAD, color: "white", fontWeight: 700 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={bulkEnableFlash}
                    sx={{ background: BRAND_GRAD, textTransform: "none", fontWeight: 600, borderRadius: 1.5, boxShadow: "none" }}
                  >
                    Enable Flash
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={bulkDisableFlash}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                  >
                    Disable Flash
                  </Button>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Discount %"
                      value={bulkDiscount}
                      onChange={(e) => setBulkDiscount(e.target.value)}
                      inputProps={{ min: 1, max: 90 }}
                      sx={{ width: 110, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    />
                    <TextField
                      size="small"
                      type="datetime-local"
                      label="Start"
                      value={bulkStart}
                      onChange={(e) => setBulkStart(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    />
                    <TextField
                      size="small"
                      type="datetime-local"
                      label="End"
                      value={bulkEnd}
                      onChange={(e) => setBulkEnd(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={bulkApplyFields}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                    >
                      Apply
                    </Button>
                  </Stack>

                  <Box sx={{ ml: "auto" }}>
                    <Button
                      size="small"
                      onClick={() => setSelectedIds([])}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Product table */}
              <Paper elevation={0} sx={{ overflow: "hidden", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell padding="checkbox" width={44}>
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={someVisibleSelected}
                          onChange={handleHeaderCheckbox}
                          disabled={loading || paginated.length === 0}
                        />
                      </TableCell>
                      <TableCell width={56} />
                      <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                      <TableCell width={90} sx={{ fontWeight: 700 }}>Flash</TableCell>
                      <TableCell width={90} sx={{ fontWeight: 700 }}>Order</TableCell>
                      <TableCell width={110} sx={{ fontWeight: 700 }}>Discount</TableCell>
                      <TableCell width={110} sx={{ fontWeight: 700 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <SkeletonRows />
                    ) : paginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 6 }}>
                          <Stack alignItems="center" spacing={1.5}>
                            <SearchIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                            <Typography variant="body2" color="text.secondary">
                              No products found
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginated.map((p) => {
                        const sel = p._sel || {};
                        const status = flashStatus(sel);
                        const isActive = !!sel.isFlashDeal;
                        const imgSrc = Array.isArray(p.images) && p.images.length > 0
                          ? p.images[0]
                          : p.imageUrl || null;

                        return (
                          <TableRow
                            key={p._id}
                            hover
                            selected={selectedIds.includes(p._id)}
                            sx={{
                              borderLeft: isActive ? "3px solid" : "3px solid transparent",
                              borderLeftColor: isActive ? "primary.main" : "transparent",
                              transition: "background 0.15s ease",
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={selectedIds.includes(p._id)}
                                onChange={() => handleRowCheckbox(p._id)}
                              />
                            </TableCell>

                            <TableCell sx={{ py: 1 }}>
                              <ProductThumb src={imgSrc} title={p.title} size={40} />
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200 }}>
                                {p.title}
                              </Typography>
                              {p.category?.name && (
                                <Typography variant="caption" color="text.secondary">
                                  {p.category.name}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              <Tooltip title={isActive ? "Remove from Flash Deals" : "Add to Flash Deals"}>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleFlash(p._id, !isActive)}
                                  disabled={!isActive && flashCount >= MAX_FLASH}
                                  color={isActive ? "error" : "default"}
                                >
                                  {isActive ? (
                                    <LocalFireDepartmentIcon color="warning" />
                                  ) : (
                                    <AddCircleOutlineIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                disabled={!isActive}
                                value={sel.flashDealOrder ?? ""}
                                onChange={(e) => setOrder(p._id, e.target.value)}
                                inputProps={{ min: 1, max: MAX_FLASH }}
                                placeholder="#"
                                sx={{ width: 70, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                disabled={!isActive}
                                value={sel.discountPercent ?? ""}
                                onChange={(e) => setField(p._id, "discountPercent", e.target.value)}
                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                inputProps={{ min: 1, max: 90 }}
                                sx={{ width: 90, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                              />
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                label={statusLabel[status]}
                                color={statusColor[status]}
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 11,
                                  ...(status === "active" && { background: BRAND_GRAD, color: "white" }),
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {meta
                      ? `${(meta.page - 1) * PAGE_SIZE + 1}–${Math.min(
                          (meta.page - 1) * PAGE_SIZE + pageProducts.length,
                          meta.total
                        )} of ${meta.total}`
                      : `${pageProducts.length} products`}
                  </Typography>
                  <Pagination
                    size="small"
                    page={page}
                    count={meta?.pages || 1}
                    onChange={(_, p) => setPage(p)}
                    shape="rounded"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        background: BRAND_GRAD,
                        color: "white",
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Stack>
          </Grid>

          {/* RIGHT PANEL — current flash list */}
          <Grid item xs={12} lg={5}>
            <Card
              elevation={0}
              sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                position: { lg: "sticky" },
                top: { lg: 24 },
              }}
            >
              <Box sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>
                    Active Flash Deals
                  </Typography>
                  <Chip
                    label={`${flashCount} / ${MAX_FLASH}`}
                    size="small"
                    sx={{ background: BRAND_GRAD, color: "white", fontWeight: 700 }}
                  />
                </Stack>
              </Box>

              {allFlashDeals.length === 0 ? (
                <Box sx={{ py: 8, textAlign: "center" }}>
                  <LocalFireDepartmentIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">No flash deals active yet</Typography>
                  <Typography variant="caption" color="text.disabled">Toggle the fire icon to add products</Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 520, overflowY: "auto" }}>
                  {(flashListOverride ?? allFlashDeals).map(({ id, product, sel }, index) => {
                    const imgSrc = Array.isArray(product.images) && product.images.length > 0
                      ? product.images[0] : product.imageUrl || null;
                    const status = flashStatus(sel);
                    const isOver = dragOverFlashId === id;

                    return (
                      <Box
                        key={id}
                        draggable
                        onDragStart={(e) => {
                          draggedFlashIdRef.current = id;
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverFlashId(id); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromId = draggedFlashIdRef.current;
                          if (!fromId || fromId === id) { setDragOverFlashId(null); return; }
                          const list = [...(flashListOverride ?? allFlashDeals)];
                          const fromIdx = list.findIndex((x) => x.id === fromId);
                          const toIdx = list.findIndex((x) => x.id === id);
                          if (fromIdx === -1 || toIdx === -1) { setDragOverFlashId(null); return; }
                          const [moved] = list.splice(fromIdx, 1);
                          list.splice(toIdx, 0, moved);
                          setFlashListOverride(list);
                          setFlashOrderDirty(true);
                          draggedFlashIdRef.current = null;
                          setDragOverFlashId(null);
                        }}
                        onDragEnd={() => { draggedFlashIdRef.current = null; setDragOverFlashId(null); }}
                        sx={{
                          px: 2, py: 1.5,
                          display: "flex", alignItems: "center", gap: 1.5,
                          borderBottom: "1px solid", borderColor: "divider",
                          "&:last-child": { borderBottom: 0 },
                          cursor: "grab",
                          userSelect: "none",
                          bgcolor: isOver ? "action.selected" : "transparent",
                          outline: isOver ? "2px solid" : "none",
                          outlineColor: "primary.main",
                          transition: "background 0.12s",
                          "&:hover": { bgcolor: isOver ? "action.selected" : "action.hover" },
                        }}
                      >
                        <DragIndicatorIcon sx={{ color: "text.disabled", fontSize: 18, cursor: "grab" }} />
                        <Typography variant="caption" fontWeight={700} color="text.disabled" sx={{ minWidth: 20, textAlign: "center" }}>
                          {index + 1}
                        </Typography>
                        <ProductThumb src={imgSrc} title={product.title} size={44} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{product.title}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center" mt={0.25}>
                            <Chip label={`${sel.discountPercent}% off`} size="small" sx={{ height: 18, fontSize: 10, background: BRAND_GRAD, color: "white", fontWeight: 700 }} />
                            <Chip label={statusLabel[status]} size="small" color={statusColor[status]} sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                          </Stack>
                        </Box>
                        <Tooltip title="Remove from flash deals">
                          <IconButton size="small" onClick={() => removeFromFlash(id)} sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}>
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Date range editor for selected product */}
              {allFlashDeals.length > 0 && (
                <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                    Edit schedule for each deal in the table on the left
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<SaveIcon />}
                    onClick={saveChanges}
                    disabled={saving}
                    sx={{
                      background: BRAND_GRAD,
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                  >
                    {saving ? "Saving…" : "Save Flash Deals"}
                  </Button>
                  {flashOrderDirty && (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<DragIndicatorIcon />}
                      onClick={saveFlashOrder}
                      disabled={savingFlashOrder}
                      sx={{ fontWeight: 700, textTransform: "none", borderRadius: 2, mt: 1 }}
                    >
                      {savingFlashOrder ? "Saving order…" : "Save Display Order"}
                    </Button>
                  )}
                </Box>
              )}
            </Card>
          </Grid>

          {/* ── QUICK PICKS PANEL ── */}
          <Grid item xs={12} lg={5}>
            <Card elevation={0} sx={{ bgcolor: "background.paper", borderRadius: 2, border: "1px solid", borderColor: "divider", position: { lg: "sticky" }, top: { lg: 24 } }}>
              <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Box sx={{ background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", borderRadius: 1.5, p: 0.75, display: "flex" }}>
                    <LocalFireDepartmentIcon sx={{ color: "white", fontSize: 18 }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700}>Quick Picks</Typography>
                  <Chip label={`${allQuickPicks.length} active`} size="small" sx={{ background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", color: "white", fontWeight: 700 }} />
                </Stack>
                <TextField
                  size="small" fullWidth placeholder="Search to add products…"
                  value={qpSearch} onChange={(e) => setQpSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                {qpSearch.trim() && (
                  <Box sx={{ mt: 1, maxHeight: 200, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                    {pageProducts.filter((p) => !p.isQuickPick && p.title.toLowerCase().includes(qpSearch.toLowerCase())).slice(0, 10).map((p) => (
                      <Box key={p._id} sx={{ px: 2, py: 1, display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: 0 }, "&:hover": { bgcolor: "action.hover" } }}>
                        <ProductThumb src={p.images?.[0]} title={p.title} size={36} />
                        <Typography variant="body2" sx={{ flex: 1 }} noWrap>{p.title}</Typography>
                        <Tooltip title="Add to Quick Picks">
                          <IconButton size="small" onClick={() => addToQuickPicks(p)} sx={{ "&:hover": { color: "success.main" } }}>
                            <AddCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {allQuickPicks.length === 0 ? (
                <Box sx={{ py: 8, textAlign: "center" }}>
                  <LocalFireDepartmentIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">No quick picks active yet</Typography>
                  <Typography variant="caption" color="text.disabled">Search above to add products</Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 500, overflowY: "auto" }}>
                  {(qpListOverride ?? allQuickPicks).map((product, index) => {
                    const imgSrc = Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : null;
                    const isOver = dragOverQPId === product._id;
                    return (
                      <Box
                        key={product._id}
                        draggable
                        onDragStart={(e) => { draggedQPIdRef.current = product._id; e.dataTransfer.effectAllowed = "move"; }}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverQPId(product._id); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromId = draggedQPIdRef.current;
                          if (!fromId || fromId === product._id) { setDragOverQPId(null); return; }
                          const list = [...(qpListOverride ?? allQuickPicks)];
                          const fromIdx = list.findIndex((x) => x._id === fromId);
                          const toIdx = list.findIndex((x) => x._id === product._id);
                          if (fromIdx === -1 || toIdx === -1) { setDragOverQPId(null); return; }
                          const [moved] = list.splice(fromIdx, 1);
                          list.splice(toIdx, 0, moved);
                          setQpListOverride(list);
                          setQpOrderDirty(true);
                          draggedQPIdRef.current = null;
                          setDragOverQPId(null);
                        }}
                        onDragEnd={() => { draggedQPIdRef.current = null; setDragOverQPId(null); }}
                        sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: 0 }, cursor: "grab", userSelect: "none", bgcolor: isOver ? "action.selected" : "transparent", outline: isOver ? "2px solid" : "none", outlineColor: "primary.main", transition: "background 0.12s", "&:hover": { bgcolor: isOver ? "action.selected" : "action.hover" } }}
                      >
                        <DragIndicatorIcon sx={{ color: "text.disabled", fontSize: 18 }} />
                        <Typography variant="caption" fontWeight={700} color="text.disabled" sx={{ minWidth: 20, textAlign: "center" }}>{index + 1}</Typography>
                        <ProductThumb src={imgSrc} title={product.title} size={44} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{product.title}</Typography>
                          <Typography variant="caption" color="text.secondary">KES {Number(product.price).toLocaleString()}</Typography>
                        </Box>
                        <Tooltip title="Remove from Quick Picks">
                          <IconButton size="small" onClick={() => removeFromQuickPicks(product._id)} sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}>
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {qpOrderDirty && (
                <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <Button variant="outlined" fullWidth startIcon={<DragIndicatorIcon />} onClick={saveQPOrder} disabled={savingQPOrder} sx={{ fontWeight: 700, textTransform: "none", borderRadius: 2 }}>
                    {savingQPOrder ? "Saving order…" : "Save Quick Picks Order"}
                  </Button>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
